import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wine, Confetti, Buildings, Gift } from "@phosphor-icons/react";
import ArtDecoDivider from "../components/ArtDecoDivider";

const HERO =
    "https://images.unsplash.com/photo-1623408859815-22534357b3db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxiYXJ0ZW5kZXIlMjBwb3VyaW5nJTIwY29ja3RhaWx8ZW58MHx8fHwxNzc5NDY5NjAxfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
    {
        icon: Wine,
        title: "Cocktail Bar Service",
        copy: "Classic and seasonal cocktails, poured with precision. You stock the bar — we run it, with the craft and composure your guests will remember.",
        testid: "service-cocktail-bar",
    },
    {
        icon: Confetti,
        title: "Weddings & Receptions",
        copy: "Signature drink design, timed service for the cocktail hour, and a steady hand behind the bar from the first toast to the last.",
        testid: "service-weddings",
    },
    {
        icon: Buildings,
        title: "Corporate Events",
        copy: "Polished, discreet bar service for openings, dinners, and client gatherings. We arrive on time, read the room, and keep the evening moving.",
        testid: "service-corporate",
    },
    {
        icon: Gift,
        title: "Private Parties",
        copy: "Birthdays, anniversaries, holidays — we tend the bar so you can be the host instead of the bartender.",
        testid: "service-private",
    },
];

export default function Services() {
    return (
        <div className="bg-obsidian min-h-screen" data-testid="page-services">
            {/* HERO */}
            <section className="relative h-[60vh] min-h-[420px] flex items-center overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO} alt="Bartender pouring cocktail" className="w-full h-full object-cover opacity-60" />
                    <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/80 to-obsidian/30" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
                    <div className="text-gold text-xs tracking-overline uppercase mb-4 flex items-center gap-3">
                        <span className="w-10 h-px bg-gold" />
                        Services
                    </div>
                    <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl mb-4 max-w-3xl">
                        For every <em className="gold-italic">occasion</em> that asks for more
                    </h1>
                </div>
            </section>

            {/* INTRO */}
            <section className="py-20 px-6 lg:px-12 text-center">
                <div className="max-w-3xl mx-auto">
                    <p className="text-white/75 text-lg md:text-xl font-light leading-relaxed">
                        The Spirited Gentleman is a mobile bartending service built on craft,
                        composure, and quiet hospitality. We arrive prepared, we stay
                        attentive, and we leave the room better than we found it.
                    </p>
                    <ArtDecoDivider />
                    <p className="text-white/55 font-light max-w-2xl mx-auto">
                        Below — a few of the evenings we know well. If yours isn't listed, ask.
                        The right answer to "can you?" is usually yes.
                    </p>
                </div>
            </section>

            {/* HOW IT WORKS — what you supply vs. what we bring */}
            <section className="px-6 lg:px-12 pb-12" data-testid="how-it-works">
                <div className="max-w-5xl mx-auto bg-obsidian-soft border border-gold/20 p-8 md:p-12">
                    <div className="text-gold text-xs tracking-overline uppercase mb-4 text-center">
                        How It Works
                    </div>
                    <h2 className="font-display text-white text-3xl md:text-4xl text-center mb-3">
                        You stock the bar. <em className="gold-italic">We run it.</em>
                    </h2>
                    <p className="text-white/55 text-sm md:text-base font-light text-center max-w-2xl mx-auto mb-10">
                        Use our Bar Calculator to build a tailored shopping list. You'll
                        provide the goods — we'll show up with the bar, the tools, and the
                        sparkling personality.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
                        <div className="bg-obsidian p-6 md:p-8" data-testid="you-bring">
                            <div className="text-xs tracking-overline uppercase text-white/40 mb-4">
                                You Bring
                            </div>
                            <ul className="space-y-2 font-sans text-white/80 text-sm md:text-base">
                                <li>· Spirits, beer, wine &amp; champagne</li>
                                <li>· Mixers &amp; juices</li>
                                <li>· Ice</li>
                                <li>· Fresh fruit &amp; garnishes</li>
                                <li>· Glassware</li>
                            </ul>
                        </div>
                        <div className="bg-obsidian p-6 md:p-8" data-testid="we-bring">
                            <div className="text-xs tracking-overline uppercase text-gold mb-4">
                                We Bring
                            </div>
                            <ul className="space-y-2 font-sans text-white/80 text-sm md:text-base">
                                <li>· The mobile bar setup</li>
                                <li>· Shakers, strainers, jiggers &amp; bar spoons</li>
                                <li>· Pour spouts, straws &amp; cocktail napkins</li>
                                <li>· House-made simple syrup, bitters &amp; sweet vermouth</li>
                                <li>· Coolers to keep everything chilled</li>
                                <li>· A sparkling personality behind the bar</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICE TILES — editorial asymmetric */}
            <section className="px-6 lg:px-12 pb-20">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
                    {SERVICES.map((s, idx) => {
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.title}
                                data-testid={s.testid}
                                className="bg-obsidian-soft p-10 md:p-12 hover:bg-[#191919] transition-all duration-500 group border-t border-l border-white/0 hover:border-gold/30"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <Icon size={32} weight="thin" className="text-gold" />
                                    <span className="font-display italic text-xl text-gold/60">
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <h3 className="font-display text-white text-2xl md:text-3xl mb-4">
                                    {s.title}
                                </h3>
                                <p className="text-white/60 font-light leading-relaxed">{s.copy}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-6 lg:px-12 text-center border-t border-white/5">
                <div className="text-gold text-xs tracking-overline uppercase mb-4">
                    Ready When You Are
                </div>
                <h2 className="font-display text-white text-4xl md:text-5xl mb-8">
                    Let's plan your <em className="gold-italic">evening</em>
                </h2>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/book" className="btn-gold" data-testid="services-cta-reserve">
                        Reserve The Evening
                    </Link>
                    <a
                        href="mailto:TheSpiritedGent@gmail.com"
                        className="btn-gold-outline"
                        data-testid="services-cta-email"
                    >
                        Email Us
                        <ArrowRight size={16} weight="thin" className="ml-2" />
                    </a>
                </div>
            </section>
        </div>
    );
}
